import { useState } from "react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import api, { fmtErr } from "@/lib/api";
import { PROFILE } from "@/data/profile";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      toast.success("Message received — thank you. I read everything from this desk.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  const rows = [
    { label: "Email", value: PROFILE.email, href: `mailto:${PROFILE.email}` },
    { label: "Phone", value: PROFILE.phone, href: `tel:${PROFILE.phoneHref}` },
    { label: "LinkedIn", value: PROFILE.linkedinLabel, href: PROFILE.linkedinUrl, external: true },
    { label: "Location", value: PROFILE.location },
  ];

  return (
    <main className="pt-28 lg:pt-36">
      <section className="wrap grid gap-16 pb-24 lg:grid-cols-2 lg:gap-20 lg:py-16">
        <Reveal>
          <p className="overline-mono">Contact</p>
          <h1 className="font-serif text-4xl leading-[1.08] tracking-tight text-navy sm:text-5xl lg:text-6xl">
            Pressure-test a decision with me.
          </h1>
          <p className="mt-6 max-w-md leading-relaxed text-navy-soft">
            Research collaborations, advisory engagements, speaking or writing invitations — the desk is
            open. I read everything.
          </p>

          <div className="mt-12 border-t border-line">
            {rows.map((r) => (
              <div key={r.label} className="grid grid-cols-[110px_1fr] items-baseline gap-4 border-b border-line py-5">
                <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-navy-mute">{r.label}</p>
                {r.href ? (
                  <a
                    href={r.href}
                    target={r.external ? "_blank" : undefined}
                    rel={r.external ? "noopener noreferrer" : undefined}
                    className="break-all text-navy transition-colors duration-300 hover:text-blue"
                  >
                    {r.value}
                  </a>
                ) : (
                  <p className="text-navy">{r.value}</p>
                )}
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <form onSubmit={submit} className="border border-line bg-paper-band p-8 sm:p-10">
            <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-blue">Write to the desk</p>

            <label className="mt-8 block">
              <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-navy-soft">Name</span>
              <input
                data-testid="contact-form-name-input"
                required
                value={form.name}
                onChange={set("name")}
                className="admin-input"
                placeholder="Your name"
              />
            </label>

            <label className="mt-6 block">
              <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-navy-soft">Email</span>
              <input
                data-testid="contact-form-email-input"
                type="email"
                required
                value={form.email}
                onChange={set("email")}
                className="admin-input"
                placeholder="you@organisation.com"
              />
            </label>

            <label className="mt-6 block">
              <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-navy-soft">Subject — optional</span>
              <input
                data-testid="contact-form-subject-input"
                value={form.subject}
                onChange={set("subject")}
                className="admin-input"
                placeholder="Research collaboration, advisory, speaking…"
              />
            </label>

            <label className="mt-6 block">
              <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-navy-soft">Message</span>
              <textarea
                data-testid="contact-form-message-input"
                required
                rows={6}
                value={form.message}
                onChange={set("message")}
                className="admin-input resize-y"
                placeholder="Tell me what you're working on…"
              />
            </label>

            <button data-testid="contact-form-submit-button" disabled={busy} className="btn-primary mt-8 w-full justify-center disabled:opacity-50">
              {busy ? "Sending…" : "Send the message"}
            </button>
          </form>
        </Reveal>
      </section>
    </main>
  );
}